/**
 * TracePanel — ranked hypotheses for a maintenance message (CONTRACT §3 trace).
 *
 * Rank is encoded three ways: position in the list, the printed rank number,
 * and the violet rank swatch that matches the 3D fill. "Check task" is the one
 * orange decide action on this surface.
 *
 * Props:
 *   trace       { faultId, fault, phase, data, reason, fallbackIds }
 *   tree        buildTree() result
 *   onSelect    (id) => void
 *   onHover     (id|null) => void — lights the part in 3D
 *   onOpenTask  (cadId, taskId) => void
 *   onRetry     () => void
 *   onExit      () => void
 *   domain      optional asset domain; 'industrial' reads "System V32", not "ATA"
 *   knowledgeNote optional provenance line for the knowledge pack (header)
 */

import { memo } from 'react';
import { X, ClipboardCheck } from 'lucide-react';
import {
  statusFor, traceRankColor, connectionType, TRACE_RANK_RAMP, TRACE_CONTEXT,
} from '../diagnosticPalette.js';
import { codeLabelFor } from '../../../config/assets.js';
import './KnowledgeCards.css';

const ROLE_LABEL = { cause: 'Likely cause', check: 'Check', affected: 'Affected' };

function TracePanelImpl({
  trace, tree, onSelect, onHover, onOpenTask, onRetry, onExit, domain = null, knowledgeNote = null,
}) {
  const { phase, data, reason, fault: localFault, fallbackIds = [] } = trace;
  const fault = data?.fault ?? localFault ?? { id: trace.faultId, title: '' };
  const sev = statusFor(fault.severity);
  const types = [...new Set((data?.paths ?? []).map((p) => p.type))];

  return (
    <section className="kc kc--trace" aria-label={`Fault trace ${fault.id}`}>
      <header className="kc__head">
        <div className="kc__head-main">
          <span className="kc__kicker">Fault trace</span>
          <h3 className="kc__title">
            <span className="kc__sev" style={{ color: sev.color }} aria-hidden="true">{sev.icon}</span>
            <code>{fault.id}</code> {fault.title}
          </h3>
          <span className="kc__sub">{sev.label}{fault.ata ? ` · ${codeLabelFor(domain)} ${fault.ata}` : ''}</span>
          {knowledgeNote && <span className="kc__sub kc__note">{knowledgeNote}</span>}
        </div>
        <button type="button" className="kc__close" onClick={onExit} aria-label="Exit fault trace (Esc)" title="Exit trace (Esc)">
          <X size={15} aria-hidden="true" />
        </button>
      </header>

      <div className="kc__body">
        {data?.fault?.description && <p className="kc__text">{data.fault.description}</p>}

        {phase === 'loading' && (
          <div className="kc__loading" role="status" aria-live="polite">
            <div className="kc__skel" /><div className="kc__skel kc__skel--short" />
            <span>Tracing suspects…</span>
          </div>
        )}

        {phase === 'error' && (
          <div className="kc__state" role="status">
            <p className="kc__state-title">
              {reason === 'not_found' ? 'No trace recorded for this message'
                : reason === 'timeout' ? 'Knowledge service did not respond in time'
                  : 'Knowledge service unavailable'}
            </p>
            <p className="kc__text kc__text--dim">
              {fallbackIds.length > 0
                ? 'Showing the part this message is mapped to in the local fault table instead.'
                : 'The model and fault list still work.'}
            </p>
            {fallbackIds.length > 0 && (
              <ul className="kc__chips">
                {fallbackIds.map((id) => (
                  <li key={id}>
                    <button type="button" className="kc__chip" onClick={() => onSelect(id)}>
                      {tree?.byId?.get(id)?.name ?? id}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {reason !== 'not_found' && (
              <button type="button" className="exview__btn" onClick={onRetry}>Try again</button>
            )}
          </div>
        )}

        {phase === 'ready' && data && (
          <>
            <ol className="kc__hyps">
              {data.hypotheses.map((h, index) => {
                const known = tree?.byId?.has(h.cad_id);
                return (
                  <li
                    key={`${index}-${h.cad_id}`}
                    className="kc__hyp"
                    onMouseEnter={() => known && onHover(h.cad_id)}
                    onMouseLeave={() => onHover(null)}
                  >
                    <div className="kc__hyp-head">
                      <span className="kc__rank" style={{ background: traceRankColor(h.rank) }} aria-label={`Rank ${h.rank}`}>
                        {h.rank}
                      </span>
                      <button
                        type="button"
                        className="kc__hyp-name"
                        onClick={() => known && onSelect(h.cad_id)}
                        onFocus={() => known && onHover(h.cad_id)}
                        onBlur={() => onHover(null)}
                        disabled={!known}
                      >
                        {h.name}
                      </button>
                      <span className="kc__role">{ROLE_LABEL[h.role] ?? h.role}</span>
                    </div>
                    {h.failure_mode?.mode && (
                      <p className="kc__fm">
                        <span className="kc__label">Failure mode</span> {h.failure_mode.mode}
                        {h.failure_mode.detection && <span className="kc__detect">{h.failure_mode.detection}</span>}
                      </p>
                    )}
                    {h.rationale && <p className="kc__text kc__text--dim">{h.rationale}</p>}
                    {h.check_task?.id && (
                      <button
                        type="button"
                        // Ranks can tie; only the first listed suspect gets the one decide action.
                        className={`kc__task${index === 0 ? ' kc__task--primary' : ''}`}
                        onClick={() => onOpenTask(h.cad_id, h.check_task.id)}
                        aria-label={`Check task: ${h.check_task.title ?? h.check_task.id}`}
                      >
                        <ClipboardCheck size={13} aria-hidden="true" />
                        <span>Check task</span>
                        <span className="kc__task-title">{h.check_task.title ?? h.check_task.id}</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>

            <div className="kc__legend" aria-label="Trace legend">
              <div className="kc__legend-row">
                <span className="kc__label">Suspicion</span>
                {TRACE_RANK_RAMP.map((c, i) => (
                  <span key={c} className="kc__legend-item">
                    <span className="kc__swatch" style={{ background: c }} aria-hidden="true" />
                    {i === TRACE_RANK_RAMP.length - 1 ? `${i + 1}+` : i + 1}
                  </span>
                ))}
                {data.part_ids.some((id) => !data.hypotheses.some((h) => h.cad_id === id)) && (
                  <span className="kc__legend-item">
                    <span className="kc__swatch" style={{ background: TRACE_CONTEXT }} aria-hidden="true" />
                    On path
                  </span>
                )}
              </div>
              {types.length > 0 && (
                <div className="kc__legend-row">
                  <span className="kc__label">Paths</span>
                  {types.map((t) => {
                    const spec = connectionType(t);
                    return (
                      <span key={t} className="kc__legend-item">
                        <span className="kc__line" style={{ background: spec.color }} aria-hidden="true" />
                        {spec.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <footer className="kc__foot">
        {data?.provenance || 'Suspects ranked from the part knowledge pack'} · Esc exits trace
      </footer>
    </section>
  );
}

export const TracePanel = memo(TracePanelImpl);
export default TracePanel;
